export const equationSpecVersion = "strategy-equation-specs-0.1.0";

export const aggregationModes = {
  MEAN: "mean",
  PRODUCT: "product"
};

export const canonicalEquationSpecs = [
  {
    id: "master-strategic-viability",
    formula:
      "SV(a|s)=sourceGate*feasibleGate*lawfulGate*mean(advantage,information,timing,position,energy,harmony)/(1+mean(cost,risk,uncertainty,exposure,disorder))",
    positive: ["advantage", "information", "timing", "position", "energy", "harmony"],
    drag: ["cost", "risk", "uncertainty", "exposure", "disorder"],
    gates: ["sourceGate", "feasibleGate", "lawfulGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Master viability is an operator score over bounded variables and hard gates; it is not a proof or prediction."
  },
  {
    id: "shih-configuration-force",
    formula:
      "S=gate*product(position,timing,coordination,morale,information,momentum)/(1+mean(friction,disorder,misalignment))",
    positive: ["position", "timing", "coordination", "morale", "information", "momentum"],
    drag: ["friction", "disorder", "misalignment"],
    gates: ["gate"],
    positiveAggregation: aggregationModes.PRODUCT,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Configuration force is an operator model for positional leverage and coordination, not a mystical or guaranteed force."
  },
  {
    id: "no-battle-victory",
    formula:
      "NBV=product(objectiveGain,conflictAvoided,preservedResources)/(1+mean(cost,retaliationRisk,collateralDamage))",
    positive: ["objectiveGain", "conflictAvoided", "preservedResources"],
    drag: ["cost", "retaliationRisk", "collateralDamage"],
    gates: [],
    positiveAggregation: aggregationModes.PRODUCT,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "No-battle victory scores preserved resources and avoided conflict without proving universal non-engagement."
  },
  {
    id: "timing-window",
    formula: "TW=product(opportunity,readiness,tempoFit)/(1+mean(delayCost,prematureRisk,noise))",
    positive: ["opportunity", "readiness", "tempoFit"],
    drag: ["delayCost", "prematureRisk", "noise"],
    gates: [],
    positiveAggregation: aggregationModes.PRODUCT,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Timing-window scores require current facts; source text alone cannot establish a live timing claim."
  },
  {
    id: "terrain-position-leverage",
    formula:
      "PL=mean(controlPoints,mobility,visibility,defensibility)/(1+mean(bottlenecks,exposure,escapeConstraint))",
    positive: ["controlPoints", "mobility", "visibility", "defensibility"],
    drag: ["bottlenecks", "exposure", "escapeConstraint"],
    gates: [],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Position leverage is an operator model and still requires domain-specific terrain or market-structure facts."
  },
  {
    id: "information-asymmetry",
    formula:
      "IA=mean(enemyRevealed,selfConcealed,signalQuality)/(1+mean(exposure,deceptionBackfire,falseSignals))",
    positive: ["enemyRevealed", "selfConcealed", "signalQuality"],
    drag: ["exposure", "deceptionBackfire", "falseSignals"],
    gates: [],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Information asymmetry is limited to lawful visibility and signal quality; it does not authorize deception or surveillance."
  },
  {
    id: "calculations-quality",
    formula:
      "CQ=mean(verifiedImprovementPerIteration)/(1+mean(iterationCost,overfitting,analysisParalysis))",
    positive: ["verifiedImprovementPerIteration"],
    drag: ["iterationCost", "overfitting", "analysisParalysis"],
    gates: [],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Calculation quality rewards measured improvement per iteration, not raw analysis volume or unsupported certainty."
  },
  {
    id: "guiguzi-influence-fit",
    formula:
      "IF=ethicsGate*lawfulGate*mean(access,intentKnowledge,messageTiming,credibility)/(1+mean(resistance,unknowns,exposure,misreadRisk))",
    positive: ["access", "intentKnowledge", "messageTiming", "credibility"],
    drag: ["resistance", "unknowns", "exposure", "misreadRisk"],
    gates: ["ethicsGate", "lawfulGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Influence fit is lawful-communication planning only and must not become a manipulation engine."
  },
  {
    id: "i-ching-change-kernel",
    formula:
      "ICK=sourceGate*mean(stateClarity,patternFit,lineShiftSignal,timing,contextFit,evidence)/(1+mean(noise,projection,missingContext))",
    positive: ["stateClarity", "patternFit", "lineShiftSignal", "timing", "contextFit", "evidence"],
    drag: ["noise", "projection", "missingContext"],
    gates: ["sourceGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Change kernel is a pattern/state-transition aid only, not supernatural prediction."
  },
  {
    id: "musashi-yagyu-execution-clarity",
    formula:
      "EC=sourceGate*mean(distanceControl,rhythm,perception,adaptation,ethicalControl)/(1+mean(fixation,hesitation,wrongTiming,fear))",
    positive: ["distanceControl", "rhythm", "perception", "adaptation", "ethicalControl"],
    drag: ["fixation", "hesitation", "wrongTiming", "fear"],
    gates: ["sourceGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Execution clarity is a licensed-anchor operator model until lawful source custody and validation exist."
  },
  {
    id: "zen-attention-kernel",
    formula:
      "AK=notMedicalGate*mean(directObservation,stability,nonAttachment)/(1+mean(projection,confusion,grasping,distraction))",
    positive: ["directObservation", "stability", "nonAttachment"],
    drag: ["projection", "confusion", "grasping", "distraction"],
    gates: ["notMedicalGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Attention kernel is a clarity aid only; it is not medical guidance or spiritual-attainment proof."
  },
  {
    id: "governance-fa-standards",
    formula:
      "GV=ethicsGate*mean(standardClarity,roleFit,rewardConsistency,auditability)/(1+mean(ambiguity,corruption,arbitraryPower,drift))",
    positive: ["standardClarity", "roleFit", "rewardConsistency", "auditability"],
    drag: ["ambiguity", "corruption", "arbitraryPower", "drift"],
    gates: ["ethicsGate"],
    positiveAggregation: aggregationModes.MEAN,
    dragAggregation: aggregationModes.MEAN,
    gateAggregation: aggregationModes.PRODUCT,
    outputRange: [0, 1],
    claimBoundary:
      "Governance standards model auditability and role clarity without endorsing coercive or authoritarian governance."
  }
];

export const equationSpecsById = Object.fromEntries(
  canonicalEquationSpecs.map((spec) => [spec.id, spec])
);

export function equationSpecById(equationId) {
  return equationSpecsById[equationId] ?? null;
}

export function canonicalEquationFormula(equationId) {
  const spec = equationSpecById(equationId);
  if (!spec) throw new Error(`Missing canonical equation spec: ${equationId}`);
  return spec.formula;
}

export function canonicalEquationVariables(equationId) {
  const spec = equationSpecById(equationId);
  if (!spec) throw new Error(`Missing canonical equation spec: ${equationId}`);
  return [...spec.gates, ...spec.positive, ...spec.drag];
}

export function canonicalEquationPropertySpec(equationId) {
  const spec = equationSpecById(equationId);
  if (!spec) throw new Error(`Missing canonical equation spec: ${equationId}`);
  return {
    positive: [...spec.positive],
    drag: [...spec.drag],
    gates: [...spec.gates]
  };
}

export function equationSpecSummary(specs = canonicalEquationSpecs) {
  return specs.reduce(
    (summary, spec) => {
      summary.total += 1;
      summary.variables += spec.positive.length + spec.drag.length + spec.gates.length;
      if (spec.gates.length > 0) summary.hardGated += 1;
      return summary;
    },
    { total: 0, variables: 0, hardGated: 0 }
  );
}
